<template>
  <div class="expressions">
    <div class="sidebar-container">
      <div class="sidebar">
        <button
          class="button is-light create-button"
          @click="createExpression">
          New Expression
        </button>
        <button
          class="button is-light create-button"
          @click="showFieldUsage">
          Show Field Usage
        </button>
        <button
          class="button is-light create-button"
          @click="showGenerationDiffs">
          View Generation Diffs
        </button>
        <div class="search-input">
          <b-input
            ref="searchInput"
            v-model="searchInput"
            custom-class="no-border"
            placeholder="Search Company Expressions"
            type="search"
            icon="magnify" />
        </div>

        <div
          ref="expressions-list"
          class="expression-search-results">
          <div
            v-for="expression in resultsToShow"
            :key="expression.id">
            <div
              tabindex="0"
              class="sidebar-item"
              :class="{ selected: expression.id === currentExpressionID }"
              @keydown.enter="selectExpression(expression)"
              @click="selectExpression(expression)">
              <b-tag class="id-tag">
                {{ expression.id }}
              </b-tag>
              {{ expression.name }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="currentExpression"
      class="content-container">
      <div class="header">
        <h2>
          <b-tag class="id-tag">
            {{ currentExpression?.id }}
          </b-tag>
          <b-input
            ref="currentExpressionName"
            v-model="currentExpressionName"
            class="expression-name-input"
            custom-class="no-border" />
        </h2>

        <div class="actions">
          <button
            class="button is-primary"
            :disabled="!edited"
            @click="saveUpdates">
            Save
          </button>

          <button
            class="button is-light"
            @click="getCurrentExpression">
            Discard
          </button>
        </div>
      </div>

      <div
        v-if="currentExpression"
        class="editor-content">
        <div class="actions">
          <button
            class="button"
            @click="isVisualizationOpen = true">
            Visualize
            <b-icon
              icon="graph-outline"
              class="icon" />
          </button>

          <button
            class="button"
            @click="getPreviewValue">
            Preview

            <b-icon
              icon="function"
              class="icon" />
          </button>

          <button
            class="button"
            @click="getExpressionUsage">
            View Usage

            <b-icon
              icon="chart-donut"
              class="icon" />
          </button>
        </div>
        <expression-editor :expression.sync="selectedSpec" />
      </div>
    </div>

    <div
      v-else
      class="placeholder">
      No expression Selected
    </div>

    <b-modal
      v-model="isVisualizationOpen"
      scroll="keep">
      <div
        v-if="currentExpressionID"
        class="card bg-dots">
        <expression-graph :expression-id="currentExpressionID" />
      </div>
    </b-modal>

    <UsageSummary
      :is-open.sync="isUsageOpen"
      :loading="modalLoading"
      :usage-report="usageReport"
      :attribute-name="currentExpressionName"
      :source-id="currentExpressionID"
      source-type="EXPRESSION" />

    <b-modal
      v-model="isPreviewOpen"
      width="500px"
      scroll="keep">
      <div class="card usage-modal">
        <div class="header">
          Preview
        </div>

        <div class="content">
          <div v-if="modalLoading">
            <b-skeleton
              width="100%"
              :count="1"
              height="40" />
          </div>

          <div v-else>
            <div v-if="previewData">
              <div
                v-for="(data, i) in previewData"
                :key="i"
                class="item">
                {{ data[0] }}
              </div>
            </div>

            <div v-if="previewData?.length === 0 || !previewData">
              No results
            </div>
          </div>
        </div>
      </div>
    </b-modal>

    <b-modal
      v-model="isFieldUsageOpen"
      width="800px"
      scroll="keep">
      <div class="card usage-modal">
        <div class="header">
          <div class="header-title">
            Field Usage in Expressions
          </div>
          <div class="filter-section">
            <div class="filter-label">
              Filter by View Classification:
            </div>
            <div class="filter-checkboxes">
              <b-field>
                <b-checkbox
                  v-model="selectedClassifications"
                  native-value="legacy_transaction">
                  Legacy Transaction
                </b-checkbox>
              </b-field>
              <b-field>
                <b-checkbox
                  v-model="selectedClassifications"
                  native-value="dbt_transaction">
                  DBT Transaction
                </b-checkbox>
              </b-field>
              <b-field>
                <b-checkbox
                  v-model="selectedClassifications"
                  native-value="primitive_dimension">
                  Primitive Dimension
                </b-checkbox>
              </b-field>
              <b-field>
                <b-checkbox
                  v-model="selectedClassifications"
                  native-value="consolidated_dimension">
                  Consolidated Dimension
                </b-checkbox>
              </b-field>
            </div>
          </div>
        </div>

        <div class="content">
          <div v-if="fieldUsageLoading">
            <b-skeleton
              width="100%"
              :count="5"
              height="40" />
          </div>

          <div v-else>
            <div v-if="groupedFieldUsage && Object.keys(groupedFieldUsage).length > 0">
              <div
                v-for="(usages, fieldName) in groupedFieldUsage"
                :key="fieldName"
                class="field-usage-item">
                <div class="field-name">
                  {{ fieldName }}
                </div>
                <div class="expression-list">
                  <div
                    v-for="(usage, j) in usages"
                    :key="j"
                    class="expression-container">
                    <div
                      class="expression-item"
                      :class="{ clickable: usage.referencer_type === 'EXPRESSION' }"
                      @click="usage.referencer_type === 'EXPRESSION' ? handleExpressionClick(usage) : null">
                      <b-tag class="id-tag">
                        {{ usage.referencer_id }}
                      </b-tag>
                      <b-tag :class="getTypeClass(usage.referencer_type)">
                        {{ formatType(usage.referencer_type) }}
                      </b-tag>
                      <span class="expression-name">{{ usage.referencer_name }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="!fieldUsageData || fieldUsageData.length === 0">
              No field usage data found
            </div>
          </div>
        </div>
      </div>
    </b-modal>

    <b-modal
      v-model="isGenerationDiffsOpen"
      width="1000px"
      scroll="keep">
      <div class="card usage-modal">
        <div class="header">
          <div class="header-title">
            Generation Diffs
          </div>
          <div class="filter-section">
            <div class="filter-label">
              Generator Version:
            </div>
            <b-field>
              <b-select
                v-model="selectedGeneratorVersion"
                @input="fetchGenerationDiffs">
                <option value="LEGACY_GENERATOR">
                  Legacy Generator (Merged)
                </option>
                <option value="DBT_GENERATOR">
                  DBT Generator
                </option>
                <option value="ESSENTIAL_PACKAGE_GENERATOR">
                  Essentials Package
                </option>
                <option value="ACCOUNTING_PACKAGE_GENERATOR">
                  Accounting Package
                </option>
              </b-select>
            </b-field>
          </div>
        </div>

        <div class="content">
          <div v-if="generationDiffsLoading">
            <b-skeleton
              width="100%"
              :count="5"
              height="40" />
          </div>

          <div v-else>
            <div v-if="generationDiffsData && generationDiffsData.summary">
              <!-- Summary Section -->
              <div class="summary-section">
                <h3>Summary</h3>
                <div class="summary-stats">
                  <div class="stat-item">
                    <span class="stat-label">Total Seed Expressions:</span>
                    <span class="stat-value">{{ generationDiffsData.summary.total_seed_expressions }}</span>
                  </div>
                  <div class="stat-item">
                    <span class="stat-label">Matching:</span>
                    <span class="stat-value is-success">{{ generationDiffsData.summary.matching }}</span>
                  </div>
                  <div class="stat-item">
                    <span class="stat-label">Different:</span>
                    <span class="stat-value is-warning">{{ generationDiffsData.summary.different }}</span>
                  </div>
                  <div class="stat-item">
                    <span class="stat-label">Missing:</span>
                    <span class="stat-value is-danger">{{ generationDiffsData.summary.missing }}</span>
                  </div>
                </div>
              </div>

              <!-- Differences Section -->
              <div
                v-if="generationDiffsData.differences && generationDiffsData.differences.length > 0"
                class="accordion-section">
                <div
                  class="accordion-header"
                  @click="isDifferencesOpen = !isDifferencesOpen">
                  <b-icon
                    :icon="isDifferencesOpen ? 'chevron-down' : 'chevron-right'"
                    size="is-small" />
                  <span class="accordion-title">
                    Differences ({{ generationDiffsData.differences.length }})
                  </span>
                </div>
                <b-collapse v-model="isDifferencesOpen">
                  <div class="accordion-content">
                    <div
                      v-for="(diff, i) in generationDiffsData.differences"
                      :key="'diff-' + i"
                      class="diff-item">
                      <div class="diff-header">
                        <div class="diff-name">
                          <b-tag class="id-tag">
                            {{ diff.expression_id }}
                          </b-tag>
                          <span
                            :class="{ 'name-differs': diff.name_differs }"
                            class="expression-name">
                            {{ diff.name }}
                          </span>
                          <b-tag
                            v-if="diff.name_differs"
                            type="is-warning"
                            size="is-small">
                            Name Changed
                          </b-tag>
                        </div>
                        <div class="diff-reason">
                          {{ diff.reason }}
                        </div>
                      </div>
                      <div class="diff-specs">
                        <div class="spec-column">
                          <div class="spec-header">
                            <div class="spec-label">
                              Company Spec
                            </div>
                            <button
                              class="button is-small is-primary"
                              @click="saveDiffSpec(diff)">
                              Save to Company
                            </button>
                          </div>
                          <div class="spec-content editor">
                            <expression-editor
                              :expression.sync="editableSpecs[diff.expression_id]" />
                          </div>
                        </div>
                        <div class="spec-column">
                          <div class="spec-header">
                            <div class="spec-label">
                              Seed Spec
                            </div>
                            <button
                              class="button is-small is-light"
                              @click="applySeedSpec(diff)">
                              Apply to Company
                            </button>
                          </div>
                          <pre class="spec-content">{{ formatSpec(diff.seed_spec) }}</pre>
                        </div>
                      </div>
                    </div>
                  </div>
                </b-collapse>
              </div>

              <!-- Missing Section -->
              <div
                v-if="generationDiffsData.missing && generationDiffsData.missing.length > 0"
                class="accordion-section">
                <div
                  class="accordion-header"
                  @click="isMissingOpen = !isMissingOpen">
                  <b-icon
                    :icon="isMissingOpen ? 'chevron-down' : 'chevron-right'"
                    size="is-small" />
                  <span class="accordion-title">
                    Missing ({{ generationDiffsData.missing.length }})
                  </span>
                </div>
                <b-collapse v-model="isMissingOpen">
                  <div class="accordion-content">
                    <div
                      v-for="(missing, i) in generationDiffsData.missing"
                      :key="'missing-' + i"
                      class="missing-item">
                      <b-tag class="id-tag">
                        {{ missing.generator_id }}
                      </b-tag>
                      <span class="expression-name">{{ missing.name }}</span>
                      <span class="missing-reason">{{ missing.reason }}</span>
                    </div>
                  </div>
                </b-collapse>
              </div>

              <!-- Matching Section -->
              <div
                v-if="generationDiffsData.matching && generationDiffsData.matching.length > 0"
                class="accordion-section">
                <div
                  class="accordion-header"
                  @click="isMatchingOpen = !isMatchingOpen">
                  <b-icon
                    :icon="isMatchingOpen ? 'chevron-down' : 'chevron-right'"
                    size="is-small" />
                  <span class="accordion-title">
                    Matching ({{ generationDiffsData.matching.length }})
                  </span>
                </div>
                <b-collapse v-model="isMatchingOpen">
                  <div class="accordion-content">
                    <div
                      v-for="(match, i) in generationDiffsData.matching"
                      :key="'match-' + i"
                      class="matching-item">
                      <b-tag class="id-tag">
                        {{ match.expression_id }}
                      </b-tag>
                      <span class="expression-name">{{ match.name }}</span>
                    </div>
                  </div>
                </b-collapse>
              </div>
            </div>

            <div v-else>
              No generation diffs data found
            </div>
          </div>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
  import ExpressionGraph from '@/components/expression_viewer/ExpressionGraphComponent'
  import ExpressionEditor from '@/components/expressions/ExpressionEditor'
  import { expressionProcessorMixin } from '@/components/reporting/expressionProcessorMixin'
  import UsageSummary from '@/components/usage/UsageSummary'

  import FuzzySearch from 'fuzzy-search'
  export default {
    name: 'ExpressionViewPage',
    components: {
      ExpressionGraph,
      ExpressionEditor,
      UsageSummary,
    },
    mixins: [expressionProcessorMixin,],
    props: {},
    data () {
      return {
        currentExpressionID: 0,
        currentExpression: null,

        searchInput: '',
        isVisualizationOpen: false,
        isPreviewOpen: false,
        isUsageOpen: false,
        isFieldUsageOpen: false,
        isGenerationDiffsOpen: false,
        isDifferencesOpen: true,
        isMissingOpen: false,
        isMatchingOpen: false,
        modalLoading: false,
        fieldUsageLoading: false,
        generationDiffsLoading: false,

        usageReport: [],
        previewData: [],
        fieldUsageData: {},
        generationDiffsData: [],
        editableSpecs: {},
        selectedGeneratorVersion: 'ESSENTIAL_PACKAGE_GENERATOR',
        selectedClassifications: [
          'legacy_transaction',
          'dbt_transaction',
          'primitive_dimension',
          'consolidated_dimension',
        ],

        edited: false,
      }
    },
    computed: {
      currentExpressionName: {
        get () {
          return this.currentExpression?.name
        },
        set (name) {
          this.currentExpression = { ...this.currentExpression, name, }
          this.edited = true
        },
      },

      selectedSpec: {
        get () {
          return this.currentExpression.spec
        },
        set (spec) {
          this.currentExpression = { ...this.currentExpression, spec, }
          this.edited = true
        },
      },

      searcher () {
        const searcher = new FuzzySearch(Object.values(this.expressions), [
          'name',
          'id',
        ])
        return searcher
      },
      resultsToShow () {
        return this.searcher.search(this.searchInput)
      },
      expressions () {
        return this.$store.getters['expressions/companyExpressions'] || {}
      },
      views () {
        return this.$store.getters['expressions/views'] || []
      },
      fieldOptions () {
        return this.$store.getters['expressions/fieldOptions'] || []
      },
      viewsMap () {
        const map = {}
        this.views.forEach(view => {
          if (view.key) {
            map[view.key] = view
          }
        })
        return map
      },
      fieldToViewClassificationLookup () {
        const lookup = {}

        // Build lookup from field key to view classification
        this.fieldOptions.forEach(field => {
          if (field.key && field.view) {
            const view = this.viewsMap[field.view]
            if (view && view.classification) {
              lookup[field.key] = view.classification
            }
          }
        })

        return lookup
      },
      groupedFieldUsage () {
        const grouped = {}
        // Force reactivity by accessing array length
        const selectedCount = this.selectedClassifications.length

        this.fieldUsageData.usages?.forEach(usage => {
          // Get view classification from lookup
          const viewClassification = this.fieldToViewClassificationLookup[usage.definition_key]

          // Filter by view classification if specified
          // If no classifications are selected, show nothing
          if (selectedCount === 0) {
            return
          }

          if (viewClassification && !this.selectedClassifications.includes(viewClassification)) {
            return
          }

          const fieldName = usage.definition_key
          if (!grouped[fieldName]) {
            grouped[fieldName] = []
          }
          grouped[fieldName].push(usage)
        })
        return grouped
      },
    },

    async mounted () {
      await this.$store.dispatch('expressions/initialize')

      const queryID = Number(this.$route.query.id)
      if (queryID) {
        this.currentExpressionID = queryID
        this.getCurrentExpression()
      }

      window.addEventListener('keydown', this.shortcutHandler)
    },

    beforeDestroy () {
      window.removeEventListener('keydown', this.shortcutHandler)
    },
    methods: {
      shortcutHandler (e) {
        if (e.ctrlKey && e.key === 's') {
          e.preventDefault()
          this.saveUpdates()
        } else if (e.ctrlKey && e.key === 'f') {
          e.preventDefault()
          this.$refs.searchInput.focus()
        } else if (e.ctrlKey && e.key === 'n') {
          e.preventDefault()
          this.createExpression()
        } else if (e.ctrlKey && e.key === 'p') {
          e.preventDefault()
          this.getPreviewValue()
        } else if (e.ctrlKey && e.key === 'u') {
          e.preventDefault()
          this.getExpressionUsage()
        } else if (e.ctrlKey && e.key === 'v') {
          e.preventDefault()
          this.isVisualizationOpen = true
        }
      },

      async createExpression () {
        const newExpression = await this.$store.dispatch(
          'expressions/createExpression'
        )
        this.currentExpressionID = newExpression.id

        await this.$nextTick()
        await this.$nextTick()
        await this.$nextTick()

        this.getCurrentExpression()

        this.$buefy.toast.open({
          message: `New Expression Created`,
          type: 'is-success',
        })
        this.$refs['expressions-list'].scrollTop =
          this.$refs['expressions-list'].scrollHeight
        this.$refs['currentExpressionName'].focus()
      },
      async saveUpdates () {
        await this.$store.dispatch(
          'expressions/updateExpression',
          this.currentExpression
        )

        this.$buefy.toast.open({
          message: `Saved`,
          type: 'is-success',
        })

        this.edited = false
      },
      getCurrentExpression () {
        if (this.currentExpressionID) {
          this.currentExpression = this.expressions[this.currentExpressionID]
        }
      },

      selectExpression (expression) {
        this.currentExpressionID = expression.id
        this.getCurrentExpression()
        if (Number(this.$route.query?.id) === expression.id) return
        this.$router.push({ query: { id: expression.id, }, })
      },

      async getExpressionUsage () {
        this.usageReport = []
        this.isUsageOpen = true
        this.modalLoading = true

        try {
          const response = await this.$apis.expressions.getExpressionUsage(
            this.currentExpressionID
          )
          this.usageReport = response || []
        } catch (e) {
          console.log(e)
        }

        this.modalLoading = false
      },

      async getPreviewValue () {
        this.isPreviewOpen = true
        this.modalLoading = true

        try {
          const response = await this.$apis.expressions.getExpressionPreview({
            sort_by: [],
            limit: 50,
            series: [
              {
                id: this.currentExpressionID,
                expression: this.preprocessExpression(
                  this.currentExpression.spec
                ),
              },
            ],
          })

          this.previewData = response.data
        } catch (e) {
          console.log(e)
        }

        this.modalLoading = false
      },

      async showFieldUsage () {
        this.fieldUsageData = []
        this.isFieldUsageOpen = true
        this.fieldUsageLoading = true

        try {
          const response = await this.$apis.expressions.getBulkExpressionFieldUsage()
          this.fieldUsageData = response || []
        } catch (e) {
          console.log(e)
        }

        this.fieldUsageLoading = false
      },

      async showGenerationDiffs () {
        this.isGenerationDiffsOpen = true
        await this.fetchGenerationDiffs()
      },

      async fetchGenerationDiffs () {
        this.generationDiffsData = []
        this.editableSpecs = {}
        this.generationDiffsLoading = true

        try {
          const response = await this.$apis.expressions.getGenerationDiffs(
            this.selectedGeneratorVersion
          )
          this.generationDiffsData = response || []

          // Initialize editable specs
          if (this.generationDiffsData.differences) {
            this.generationDiffsData.differences.forEach(diff => {
              this.$set(this.editableSpecs, diff.expression_id, diff.company_spec)
            })
          }
        } catch (e) {
          console.log(e)
          this.$buefy.toast.open({
            message: `Error fetching generation diffs: ${e.message}`,
            type: 'is-danger',
          })
        }

        this.generationDiffsLoading = false
      },

      async saveDiffSpec (diff) {
        try {
          const expression = this.expressions[diff.expression_id]
          if (!expression) {
            throw new Error('Expression not found')
          }

          const updatedExpression = {
            ...expression,
            spec: this.editableSpecs[diff.expression_id],
          }

          await this.$store.dispatch(
            'expressions/updateExpression',
            updatedExpression
          )

          this.$buefy.toast.open({
            message: `Expression "${diff.name}" updated successfully`,
            type: 'is-success',
          })

          // Refresh the diffs
          await this.fetchGenerationDiffs()
        } catch (e) {
          console.log(e)
          this.$buefy.toast.open({
            message: `Error saving expression: ${e.message}`,
            type: 'is-danger',
          })
        }
      },

      applySeedSpec (diff) {
        // Update the editable spec with the seed spec (frontend only)
        this.$set(this.editableSpecs, diff.expression_id, diff.seed_spec)

        this.$buefy.toast.open({
          message: `Applied seed spec to "${diff.name}". Click "Save to Company" to persist.`,
          type: 'is-info',
        })
      },

      handleExpressionClick (usage) {
        // Close modal and redirect to the expression
        this.isFieldUsageOpen = false
        this.selectExpression(this.expressions[usage.referencer_id])
      },

      formatType (type) {
        const typeMap = {
          'COMPANY_EXPRESSION': 'Expression',
          'EXPRESSION': 'Expression',
          'COMPANY_DIMENSION': 'Dimension',
          'COMPANY_MEASURE': 'Measure',
          'COMPANY_FILTER': 'Filter',
        }
        return typeMap[type] || type
      },

      getTypeClass (type) {
        const classMap = {
          'COMPANY_EXPRESSION': 'is-info',
          'EXPRESSION': 'is-info',
          'COMPANY_DIMENSION': 'is-warning',
          'COMPANY_MEASURE': 'is-success',
          'COMPANY_FILTER': 'is-danger',
        }
        return classMap[type] || 'is-light'
      },

      formatSpec (spec) {
        return JSON.stringify(spec, null, 2)
      },
    },
  }
</script>

<style lang="scss" scoped>
.expressions {
  position: relative;
  max-height: 100%;
  display: flex;
  font-size: 16px;
}
.create-button {
  border-bottom: $border-1 !important;
  font-size: 15px !important;
  width: 100%;
}
.content-container {
  // padding: 0 40px;
  display: flex;
  flex-direction: column;
  width: 100%;
  .header {
    padding: 20px 40px;
    display: flex;
    width: 100%;
    // padding-bottom: 20px;
    align-items: center;
    h2 {
      font-weight: 400;
      font-size: 20px;
      display: flex;
      align-items: center;
      .id-tag {
        font-size: 15px;
      }
    }

    .actions {
      margin-left: auto;

      .button {
        margin-left: 10px;
        min-width: 120px;
      }
    }

    border-bottom: $border-1;
  }

  .expression-name-input {
    min-width: 500px;
  }

  .editor-content {
    padding: 0 40px;
    height: 100%;
    width: 100%;
    display: flex;
    flex-direction: column;
    overflow: auto;

    .actions {
      text-align: right;
      padding-top: 30px;
      padding-bottom: 10px;

      .button {
        margin-left: 5px;

        .icon {
          margin-left: 5px;
        }
      }
    }
  }
}

.placeholder {
  display: flex;
  width: 100%;
  justify-content: center;
  align-items: center;
}

.sidebar-container {
  // padding: 20px 0 20px 20px;
}
.sidebar {
  width: 320px;
  height: 100%;
  font-size: 13px;

  background: white;

  // border-radius: 10px;
  border-right: $border-1;
  border-top: $border-1;

  display: flex;
  flex-direction: column;
}
.search-input {
  padding: 3px 8px;
  border-bottom: $border-1;
  margin: 0px;
}

.expression-search-results {
  overflow-y: scroll;
  height: 100%;
}
.sidebar-item {
  padding: 10px 20px;
  &:hover {
    cursor: pointer;
    background-color: $grayscale-8;
  }
  &.selected {
    background-color: $grayscale-7;
  }
  .id-tag {
    font-size: 12px;
    margin-right: 10px;
  }
}

.expression-canvas {
  border: $border-1;
  border-radius: 10px;
  margin: 20px;
}

.usage-modal {
  .header {
    padding: 20px;
    border-bottom: $border-1;

    .header-title {
      font-size: 18px;
      font-weight: 600;
      margin-bottom: 16px;
    }

    .filter-section {
      .filter-label {
        font-size: 14px;
        font-weight: 500;
        margin-bottom: 8px;
        color: $grayscale-2;
      }

      .filter-checkboxes {
        display: flex;
        gap: 16px;
        flex-wrap: wrap;
      }
    }
  }

  .content {
    padding: 20px;
    max-height: 600px;
    overflow-y: auto;
  }
}

.field-usage-item {
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: $border-1;

  &:last-child {
    border-bottom: none;
  }

  .field-name {
    font-weight: 600;
    font-size: 16px;
    margin-bottom: 10px;
    color: $primary;
  }

  .expression-list {
    display: flex;
    flex-direction: column;
    gap: 8px;

    .expression-container {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .expression-item {
      padding: 8px 12px;
      background-color: $grayscale-8;
      border-radius: 4px;
      transition: background-color 0.2s;
      display: flex;
      align-items: center;
      gap: 8px;

      &.clickable {
        cursor: pointer;

        &:hover {
          background-color: $grayscale-7;
        }
      }

      .id-tag {
        font-size: 12px;
      }

      .expression-name {
        flex: 1;
      }
    }
  }
}

.summary-section {
  margin-bottom: 30px;
  padding: 20px;
  background-color: $grayscale-8;
  border-radius: 8px;

  h3 {
    font-size: 18px;
    font-weight: 600;
    margin-bottom: 16px;
  }

  .summary-stats {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 16px;

    .stat-item {
      display: flex;
      flex-direction: column;
      gap: 4px;

      .stat-label {
        font-size: 13px;
        color: $grayscale-3;
      }

      .stat-value {
        font-size: 24px;
        font-weight: 600;

        &.is-success {
          color: #48c774;
        }

        &.is-warning {
          color: #ffdd57;
        }

        &.is-danger {
          color: #f14668;
        }
      }
    }
  }
}

.accordion-section {
  margin-bottom: 16px;
  border: $border-1;
  border-radius: 8px;
  overflow: hidden;

  .accordion-header {
    padding: 16px 20px;
    background-color: $grayscale-8;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 12px;
    font-weight: 500;
    transition: background-color 0.2s;

    &:hover {
      background-color: $grayscale-7;
    }

    .accordion-title {
      font-size: 16px;
    }
  }

  .accordion-content {
    padding: 16px;
  }
}

.diff-item {
  padding: 16px;
  margin-bottom: 16px;
  border: $border-1;
  border-radius: 6px;
  background-color: white;

  &:last-child {
    margin-bottom: 0;
  }

  .diff-header {
    margin-bottom: 16px;

    .diff-name {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 8px;

      .expression-name {
        font-size: 16px;
        font-weight: 600;

        &.name-differs {
          color: #ffdd57;
        }
      }

      .id-tag {
        font-size: 12px;
      }
    }

    .diff-reason {
      font-size: 13px;
      color: $grayscale-3;
    }
  }

  .diff-specs {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;

    .spec-column {
      display: flex;
      flex-direction: column;

      .spec-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 8px;

        .spec-label {
          font-size: 13px;
          font-weight: 600;
          color: $grayscale-2;
        }
      }

      .spec-content {
        background-color: $grayscale-9;
        padding: 12px;
        border-radius: 4px;
        font-size: 12px;
        line-height: 1.6;
        max-height: 400px;
        overflow-y: auto;
        font-family: 'Courier New', Courier, monospace;

        &.editor {
          padding: 0;
          background-color: white;
          border: $border-1;

          &.read-only {
            pointer-events: none;
            opacity: 0.7;
            background-color: $grayscale-9;
          }
        }
      }
    }
  }
}

.missing-item,
.matching-item {
  padding: 12px 16px;
  margin-bottom: 8px;
  background-color: $grayscale-8;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 12px;

  &:last-child {
    margin-bottom: 0;
  }

  .id-tag {
    font-size: 12px;
  }

  .expression-name {
    flex: 1;
    font-weight: 500;
  }

  .missing-reason {
    font-size: 12px;
    color: $grayscale-3;
  }
}
</style>
